/**
 * MobilePlanningTopMenu
 *
 * Mobile-optimized sticky top navigation for the Planning module.
 * Replaces the desktop top menu when isMobile === 2.
 * Uses MUI AppBar/Toolbar with backdrop blur, view toggle, and action buttons.
 */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import AddIcon from "@mui/icons-material/Add";
import Langs, { Localize } from "../../../../app/lang/langs";
import { setSelectedSamplesView } from "../../planningSlice";
import { ORANGE, TRANSITION_SMOOTH, iconButtonSx } from "./constants";
import { ViewToggle } from "./components";

/**
 * @param {Object} props
 * @param {string}   props.title            - Title text (default: "Planning")
 * @param {React.ReactNode} props.teamSelector - TeamSelector component rendered by parent
 * @param {React.ReactNode} props.datePicker   - TransparentDatePicker component rendered by parent
 * @param {string}   props.selectedView     - "WEEKLY" | "MONTHLY"
 * @param {Function} props.onViewChange     - (view: string) => void
 * @param {Object}   props.menuRef          - useRef for PrimeReact Menu
 * @param {Array}    props.menuItems        - PrimeReact Menu model items
 * @param {Array}    props.selectedShifts   - Currently selected shift IDs
 * @param {Function} props.onDelete         - Delete handler
 * @param {Function} props.onPublish        - Publish handler
 * @param {boolean}  props.isAdmin          - Whether user has manager/admin access
 * @param {boolean}  props.showPublish      - Whether to show the publish button
 */
export default function MobilePlanningTopMenu({
  title,
  teamSelector,
  datePicker,
  selectedView,
  onViewChange,
  menuRef,
  menuItems,
  selectedShifts = [],
  onDelete,
  onPublish,
  isAdmin,
  showPublish,
}) {
  const dispatch = useDispatch();
  const selectedSamplesView = useSelector((state) => state.planning.selectedSamplesView);
  const lang = useSelector((state) => state.settings.lang);

  const handleToggleSamples = () => {
    dispatch(setSelectedSamplesView(selectedSamplesView ? false : "SHIFTS"));
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        zIndex: 100,
        bgcolor: "rgba(255,255,255,0.72)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        boxShadow:
          "0 1px 8px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
        color: "text.primary",
        transition: TRANSITION_SMOOTH,
        pt: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* Row 1: Title + Action buttons */}
      <Toolbar
        disableGutters
        sx={{
          minHeight: 52,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Title */}
        <Box
          sx={{
            fontWeight: 650,
            fontSize: "1.1rem",
            letterSpacing: "-0.02em",
            transition: "color 0.2s ease",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title || <Langs str="Planning" />}
        </Box>

        {/* Right action buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          {/* Sample shifts toggle — visible for admins */}
          {isAdmin && (
            <Tooltip title={<Langs str={selectedSamplesView ? "HIDE_SAMPLES" : "ADD_SHIFT"} />}>
              <IconButton
                onClick={handleToggleSamples}
                aria-label={Localize(selectedSamplesView ? "HIDE_SAMPLES" : "ADD_SHIFT", lang)}
                size="large"
                sx={{
                  ...iconButtonSx,
                  color: selectedSamplesView ? "#fff" : ORANGE,
                  backgroundColor: selectedSamplesView ? ORANGE : "transparent",
                  "&:hover": {
                    backgroundColor: selectedSamplesView
                      ? `${ORANGE}cc`
                      : "rgba(255,144,21,0.08)",
                    transform: "scale(1.06)",
                  },
                }}
              >
                <AddIcon
                  sx={{
                    transition: "transform 0.25s ease",
                    transform: selectedSamplesView ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                />
              </IconButton>
            </Tooltip>
          )}

          {/* Delete button — visible only when shifts are selected */}
          {selectedShifts.length > 0 && (
            <Tooltip title={<Langs str="DELETE_SHIFTS" />}>
              <IconButton
                onClick={onDelete}
                aria-label={Localize("DELETE_SHIFTS", lang)}
                size="large"
                sx={{
                  ...iconButtonSx,
                  color: "#d32f2f",
                  // Extra breathing room either side: this is the only destructive
                  // control in the bar and it sits between two safe ones.
                  mx: 0.75,
                  "&:hover": {
                    backgroundColor: "rgba(211,47,47,0.08)",
                    transform: "scale(1.06)",
                  },
                }}
              >
                <Badge
                  badgeContent={selectedShifts.length}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      minWidth: 18,
                      height: 18,
                    },
                  }}
                >
                  <DeleteOutlineIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Publish button — visible only for admin/owner with unpublished shifts */}
          {showPublish && isAdmin && (
            <Tooltip title={<Langs str="PUBLISH" />}>
              <IconButton
                onClick={onPublish}
                aria-label={Localize("PUBLISH", lang)}
                size="large"
                sx={{
                  ...iconButtonSx,
                  color: ORANGE,
                  "&:hover": {
                    backgroundColor: "rgba(255,144,21,0.08)",
                    transform: "scale(1.06)",
                    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
                  },
                }}
              >
                <PublishIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Options menu button — visible only for managers */}
          {isAdmin && (
            <Tooltip title={<Langs str="Options" />}>
              <IconButton
                onClick={(event) => menuRef?.current?.toggle(event)}
                aria-label={Localize("Options", lang)}
                aria-controls="popup_menu_right"
                aria-haspopup="true"
                size="large"
                sx={iconButtonSx}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>

      {/* Row 2: TeamSelector + DatePicker */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          pb: 0.5,
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{teamSelector}</Box>
        <Box sx={{ flexShrink: 0 }}>{datePicker}</Box>
      </Box>

      {/* Row 3: Week/Month toggle */}
      <ViewToggle selectedView={selectedView} onViewChange={onViewChange} />
    </AppBar>
  );
}
